<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯客戶帳號' : '新增客戶帳號' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel" :disabled="loading">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="loading" @click="handleSubmit">
          <span v-if="loading">{{ isEdit ? '更新中...' : '建立中...' }}</span>
          <span v-else>{{ isEdit ? '更新帳號' : '建立帳號' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-account-container">

    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="clientId" class="required">客戶</label>
              <select
                id="clientId"
                v-model="form.clientId"
                class="form-control"
                :class="{ 'is-invalid': errors.clientId }"
                required
                :disabled="loadingClients || isEdit"
              >
                <option value="">請選擇客戶</option>
                <option v-for="client in clients" :key="client.id" :value="client.id">
                  {{ client.clientName }} ({{ client.clientCode }})
                </option>
              </select>
              <div v-if="errors.clientId" class="invalid-feedback">
                {{ errors.clientId }}
              </div>
              <p class="form-hint">{{ loadingClients ? '載入客戶列表中...' : '選擇此帳號所屬的客戶' }}</p>
              <p v-if="isEdit" class="form-hint">客戶不可修改</p>
            </div>

            <div class="form-group">
              <label for="role">角色</label>
              <select
                id="role"
                v-model="form.role"
                class="form-control"
                :disabled="isEdit || loadingRoles"
              >
                <option value="">請選擇角色</option>
                <option
                  v-for="item in roles"
                  :key="item.role_code"
                  :value="item.role_code"
                >
                  {{ item.role_name }}
                </option>
              </select>
              <p class="form-hint">客戶管理員每客戶僅能有一名；員工可有多名</p>
              <p v-if="isEdit" class="form-hint">帳號類型不可修改</p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="username" class="required">登入帳號</label>
              <input
                id="username"
                v-model="form.username"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.username }"
                placeholder="請輸入登入帳號"
                required
                autocomplete="username"
                :disabled="isEdit"
              />
              <div v-if="errors.username" class="invalid-feedback">
                {{ errors.username }}
              </div>
              <p v-if="isEdit" class="form-hint">登入帳號無法修改</p>
            </div>

            <div class="form-group">
              <label for="email" class="required">Email</label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                class="form-control"
                :class="{ 'is-invalid': errors.email }"
                placeholder="請輸入 Email"
                required
                autocomplete="email"
              />
              <div v-if="errors.email" class="invalid-feedback">
                {{ errors.email }}
              </div>
              <div class="alert alert-info email-notice">
                <i class="ri-mail-send-line"></i>
                <span>請務必正確填寫，系統重要通知會發送到這個 Email。同一 Email 可供多個帳號使用。</span>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="name" class="required">姓名</label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.name }"
                placeholder="請輸入姓名"
                required
              />
              <div v-if="errors.name" class="invalid-feedback">
                {{ errors.name }}
              </div>
            </div>
          </div>

          <div class="form-row single-column">
            <div class="form-group">
              <label for="password" :class="{ required: !isEdit }">密碼</label>
              <input
                id="password"
                v-model="form.password"
                type="password"
                class="form-control"
                :class="{ 'is-invalid': errors.password }"
                :placeholder="isEdit ? '留空則不修改密碼' : '請輸入密碼'"
                :required="!isEdit"
                autocomplete="new-password"
                :minlength="isEdit ? 0 : 8"
              />
              <div v-if="errors.password" class="invalid-feedback">
                {{ errors.password }}
              </div>
              <p class="form-hint">{{ isEdit ? '留空則不修改密碼。' : '' }}至少 8 碼，且需包含英文字母及符號（如 !@#$%^&* 等）</p>
            </div>
          </div>

          <div class="form-row single-column">
            <div class="form-group">
              <label for="confirmPassword" :class="{ required: !isEdit }">確認密碼</label>
              <input
                id="confirmPassword"
                v-model="form.confirmPassword"
                type="password"
                class="form-control"
                :class="{ 'is-invalid': errors.confirmPassword }"
                :placeholder="isEdit ? '留空則不修改密碼' : '請再次輸入密碼'"
                :required="!isEdit"
                autocomplete="new-password"
              />
              <div v-if="errors.confirmPassword" class="invalid-feedback">
                {{ errors.confirmPassword }}
              </div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">狀態設定</h3>
          
          <div class="form-group">
            <label for="status">帳號狀態</label>
            <select
              id="status"
              v-model="form.status"
              class="form-control"
            >
              <option value="active">啟用</option>
              <option value="inactive">停用</option>
            </select>
            <div class="alert alert-warning status-notice">
              <i class="ri-error-warning-line"></i>
              <span>停用的帳號將無法登入系統。</span>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">權限設定</h3>
          <div class="alert alert-info permission-notice">
            <i class="ri-information-line"></i>
            <span>預設為全部權限，新增後可自行變更。</span>
          </div>
          <div class="form-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="form.hasAllPermissions"
                class="form-checkbox"
                @change="handleHasAllPermissionsChange"
              />
              <span>擁有全部權限</span>
            </label>
            <p class="form-hint">
              勾選此選項後，帳號將擁有所有權限，無需再設定具體權限列表和程式功能
            </p>
          </div>

        </div>

        <!-- 程式功能設定 -->
        <div v-if="!form.hasAllPermissions" class="form-section">
          <h3 class="section-title">程式功能設定</h3>
          
          <!-- 新增模式提示 -->
          <div v-if="!isEdit" class="alert alert-info" style="margin-bottom: 1rem;">
            <i class="ri-information-line"></i>
            <span>請先保存帳號後才能設定程式功能</span>
          </div>
          
          <div class="programs-management">
            <div class="programs-header">
              <p class="programs-description">設定此帳號可以使用的程式功能（根據客戶的方案模組）</p>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                @click="handleOpenAddProgramsDialog"
                :disabled="loadingPrograms || !form.clientId || !isEdit"
              >
                <i class="ri-add-line"></i> 添加程式
              </button>
            </div>

            <div v-if="loadingPrograms" class="loading-state">
              <i class="ri-loader-4-line spin"></i> 載入中...
            </div>

            <div v-else-if="associatedPrograms.length === 0" class="empty-state">
              <i class="ri-folder-open-line"></i>
              <p>尚未設定任何程式功能</p>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                @click="handleOpenAddProgramsDialog"
                :disabled="!form.clientId || !isEdit"
              >
                <i class="ri-add-line"></i> 添加程式
              </button>
            </div>

            <div v-else class="programs-list">
              <div
                v-for="program in associatedPrograms"
                :key="program.id"
                class="program-item"
              >
                <div class="program-info">
                  <i :class="program.icon || 'ri-file-line'" class="program-icon"></i>
                  <div class="program-details">
                    <div class="program-name">{{ program.programName }}</div>
                    <div class="program-meta">
                      <span class="program-code">{{ program.programCode }}</span>
                      <span class="program-path-small">{{ program.programPath }}</span>
                    </div>
                    <!-- 顯示此帳號允許的權限 -->
                    <div class="program-allowed-permissions">
                      <div v-if="isAllPermissionsSelected(program)" class="all-perms-allowed">
                        <i class="ri-check-line"></i>
                        <span>允許所有功能</span>
                      </div>
                      <div v-else-if="program.allowedPermissions && program.allowedPermissions.length > 0" class="allowed-perms">
                        <span class="perms-label">允許功能：</span>
                        <span v-for="perm in program.allowedPermissions" :key="perm" class="perm-badge">
                          {{ formatPermissionName(perm) }}
                        </span>
                      </div>
                      <div v-else class="all-perms-allowed">
                        <i class="ri-check-line"></i>
                        <span>允許所有功能</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="program-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    @click="handleEditPermissions(program)"
                    title="編輯權限"
                    :disabled="!isEdit"
                  >
                    <i class="ri-shield-line"></i>
                    編輯權限
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-danger"
                    @click="handleRemoveProgram(program.id)"
                    :disabled="removingProgramId === program.id || !isEdit"
                  >
                    <i class="ri-delete-bin-line"></i>
                    {{ removingProgramId === program.id ? '移除中...' : '移除' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>
      </form>
    </div>

    <!-- 添加程式對話框 -->
    <ProgramSelector
      :show="showAddProgramsDialog"
      title="添加程式功能"
      confirm-button-text="添加"
      :available-programs="availablePrograms"
      :loading="loadingAvailablePrograms"
      :saving="addingPrograms"
      :initial-selected="{}"
      @close="showAddProgramsDialog = false"
      @confirm="handleAddPrograms"
    />

    <!-- 編輯權限對話框 -->
    <Teleport to="body">
      <div v-if="showEditPermissionsDialog" class="modal-overlay" @click.self="showEditPermissionsDialog = false">
        <div class="modal-dialog">
          <div class="modal-header">
            <h3 class="modal-title">編輯程式權限</h3>
            <button
              type="button"
              class="btn-close"
              @click="showEditPermissionsDialog = false"
            >
              <i class="ri-close-line"></i>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="editingProgram" class="program-info-section">
              <div class="program-info-card">
                <div class="program-header">
                  <i :class="editingProgram.icon || 'ri-file-line'" class="program-icon-large"></i>
                  <div class="program-details-full">
                    <div class="program-name-large">{{ editingProgram.programName }}</div>
                    <div class="program-meta-full">
                      <span class="program-code">{{ editingProgram.programCode }}</span>
                      <span class="program-path">{{ editingProgram.programPath }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="permissions-editor">
                <div class="permissions-header">
                  <span class="permissions-label">選擇允許的功能：</span>
                  <button
                    type="button"
                    class="btn-link btn-sm"
                    @click="selectAllEditPermissions"
                  >
                    全選
                  </button>
                </div>

                <div v-if="editingProgram.permissions && editingProgram.permissions.length > 0" class="permissions-checkboxes">
                  <label
                    v-for="perm in editingProgram.permissions"
                    :key="perm"
                    class="permission-checkbox-label"
                  >
                    <input
                      type="checkbox"
                      :checked="editPermissionsSelected === null || (Array.isArray(editPermissionsSelected) && editPermissionsSelected.includes(perm))"
                      @change="handleEditPermissionToggle(perm)"
                      :disabled="perm.endsWith(':read') && Array.isArray(editPermissionsSelected) && editPermissionsSelected.length === 1 && editPermissionsSelected.includes(perm)"
                    />
                    <span class="permission-name">{{ formatPermissionName(perm) }}</span>
                  </label>
                </div>
                <div v-else class="no-permissions">
                  <i class="ri-information-line"></i>
                  此程式沒有定義任何權限
                </div>

                <div class="permissions-hint">
                  <i class="ri-information-line"></i>
                  read 權限預設勾選且不可取消，點擊「全選」可選擇此程式的所有功能
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showEditPermissionsDialog = false"
              :disabled="updatingPermissions"
            >
              取消
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="handleSavePermissions"
              :disabled="updatingPermissions"
            >
              <span v-if="updatingPermissions">更新中...</span>
              <span v-else>儲存</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch, Teleport } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { clientAccountService } from '@/api/modules/clientAccounts.js';
import { clientService } from '@/api/modules/clients.js';
import { accountProgramService } from '@/api/modules/accountPrograms.js';
import ProgramSelector from '@/components/ProgramSelector.vue';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';
import { rolesService } from '@/api/modules/roles.js';

export default {
  name: 'CreateClientAccount',
  components: {
    ProgramSelector
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const errorMessage = ref('');
    const errors = reactive({});
    const clients = ref([]);
    const loadingClients = ref(false);
    const roles = ref([]);
    const loadingRoles = ref(false);

    const isEdit = computed(() => {
      return !!route.params.id;
    });

    const form = reactive({
      clientId: '',
      username: '',
      email: '',
      name: '',
      password: '',
      confirmPassword: '',
      role: 'client_owner',
      status: 'active',
      hasAllPermissions: true,
    });

    // 程式功能管理相關
    const loadingPrograms = ref(false);
    const associatedPrograms = ref([]);
    const showAddProgramsDialog = ref(false);
    const loadingAvailablePrograms = ref(false);
    const availablePrograms = ref([]);
    const addingPrograms = ref(false);
    const removingProgramId = ref(null);
    
    // 編輯權限相關
    const showEditPermissionsDialog = ref(false);
    const editingProgram = ref(null);
    const editPermissionsSelected = ref([]);
    const updatingPermissions = ref(false);

    const validateForm = () => {
      // 清除之前的錯誤
      Object.keys(errors).forEach(key => delete errors[key]);
      errorMessage.value = '';

      let isValid = true;

      // 驗證客戶 ID
      if (!form.clientId || form.clientId.trim().length === 0) {
        errors.clientId = '請輸入客戶 ID';
        isValid = false;
      }

      // 驗證登入帳號
      if (!form.username || form.username.trim().length < 3) {
        errors.username = '登入帳號至少需要 3 個字元';
        isValid = false;
      }

      // 驗證 Email（必填）
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!form.email || !emailRegex.test(form.email)) {
        errors.email = '請輸入有效的 Email 地址';
        isValid = false;
      }

      // 驗證姓名
      if (!form.name || form.name.trim().length === 0) {
        errors.name = '請輸入姓名';
        isValid = false;
      }

      // 密碼格式驗證函式
      const validatePassword = (pwd) => {
        if (pwd.length < 8) return '密碼至少需要 8 個字元';
        if (!/[a-zA-Z]/.test(pwd)) return '密碼必須包含至少一個英文字母';
        if (!/[^a-zA-Z0-9]/.test(pwd)) return '密碼必須包含至少一個符號（如 !@#$%^&* 等）';
        return null;
      };

      // 驗證密碼（編輯模式下，密碼是可選的）
      if (!isEdit.value) {
        // 新增模式：密碼必填
        if (!form.password) {
          errors.password = '請輸入密碼';
          isValid = false;
        } else {
          const pwdError = validatePassword(form.password);
          if (pwdError) {
            errors.password = pwdError;
            isValid = false;
          }
        }

        // 驗證確認密碼
        if (form.password !== form.confirmPassword) {
          errors.confirmPassword = '兩次輸入的密碼不一致';
          isValid = false;
        }
      } else {
        // 編輯模式：如果填了密碼，則需要驗證
        if (form.password) {
          const pwdError = validatePassword(form.password);
          if (pwdError) {
            errors.password = pwdError;
            isValid = false;
          }

          if (form.password !== form.confirmPassword) {
            errors.confirmPassword = '兩次輸入的密碼不一致';
            isValid = false;
          }
        }
      }

      return isValid;
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const accountData = {
          email: form.email.trim(),
          name: form.name.trim(),
          role: form.role,
          status: form.status,
        };

        // 編輯模式下，只有填了密碼才加入
        if (isEdit.value) {
          if (form.password) {
            accountData.password = form.password;
          }
        } else {
          // 新增模式下，需要提交 username, password 和 clientId
          accountData.username = form.username.trim();
          accountData.password = form.password;
          accountData.clientId = form.clientId.trim();
        }

        let response;
        if (isEdit.value) {
          response = await clientAccountService.updateAccount(route.params.id, accountData);
        } else {
          response = await clientAccountService.createAccount(accountData);
        }

        if (response && response.success) {
          // 使用 form.username 而不是 accountData.username（編輯模式下 accountData 沒有 username）
          const displayName = form.username || accountData.username;
          
          await showSuccess(
            isEdit.value ? '更新成功' : '新增成功',
            `客戶帳號「${displayName}」已成功${isEdit.value ? '更新' : '建立'}`
          );
          // 成功後返回列表頁
          router.push('/system/clients/accounts');
        } else {
          const errorMsg = (response && response.message) || (isEdit.value ? '更新帳號失敗' : '新增帳號失敗');
          errorMessage.value = errorMsg;
        }
      } catch (error) {
        console.error('操作失敗:', error);
        console.error('錯誤詳情:', {
          message: error.message,
          code: error.code,
          details: error.details,
          response: error.response?.data
        });
        
        // 處理特定錯誤
        if (error.code === 'USERNAME_EXISTS') {
          errors.username = '登入帳號已存在';
        } else if (error.code === 'PASSWORD_INVALID') {
          errors.password = error.message;
        } else if (error.code === 'CLIENT_NOT_FOUND') {
          errors.clientId = '客戶不存在';
        } else if (error.code === 'CLIENT_OWNER_EXISTS') {
          errors.clientId = '該客戶已有管理員帳號';
          await showError(
            '每個客戶僅能有一名管理員帳號',
            '請改選「員工」或選擇其他客戶後再送出。'
          );
        } else if (error.code === 'VALIDATION_ERROR') {
          // 顯示詳細的驗證錯誤
          if (error.details && error.details.validationErrors) {
            const validationErrors = error.details.validationErrors;
            validationErrors.forEach(err => {
              const field = err.field.replace('/body/', '').replace('/', '');
              if (field && errors[field] !== undefined) {
                errors[field] = err.message;
              }
            });
          }
          errorMessage.value = error.message || '輸入參數驗證失敗，請檢查表單內容';
        } else {
          errorMessage.value = error.message || (isEdit.value ? '更新帳號失敗，請稍後再試' : '新增帳號失敗，請稍後再試');
        }
      } finally {
        loading.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/system/clients/accounts');
    };

    // 獲取客戶列表
    const fetchClients = async () => {
      loadingClients.value = true;
      try {
        const response = await clientService.getClients({ 
          page: 1, 
          pageSize: 100,
          status: 'active'
        });
        
        if (response && response.success && response.data && response.data.items) {
          clients.value = response.data.items;
        }
      } catch (error) {
        console.error('獲取客戶列表失敗:', error);
        showError('獲取客戶列表失敗', error.message || '無法載入客戶列表，請稍後再試');
      } finally {
        loadingClients.value = false;
      }
    };

    const fetchRoles = async () => {
      loadingRoles.value = true;
      try {
        const response = await rolesService.getRolesByAccountType('client');
        if (response && response.success && response.data) {
          const rolesList = Array.isArray(response.data)
            ? response.data
            : (response.data.items || []);
          roles.value = rolesList;
        }
      } catch (error) {
        console.error('獲取角色列表失敗:', error);
      } finally {
        loadingRoles.value = false;
      }
    };

    // 獲取帳號資料（編輯模式）
    const fetchAccount = async () => {
      if (!isEdit.value) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const response = await clientAccountService.getAccount(route.params.id);

        if (response && response.success && response.data) {
          const account = response.data;
          
          form.clientId = account.clientId || '';
          form.username = account.username || '';
          form.email = account.email || '';
          form.name = account.name || '';
          form.role = account.role === 'staff' ? 'client_staff' : (account.role || 'client_owner');
          form.status = account.status || 'active';
          form.hasAllPermissions = account.hasAllPermissions || false;
          
          // 編輯模式下，密碼欄位留空
          form.password = '';
          form.confirmPassword = '';
        } else {
          errorMessage.value = (response && response.message) || '獲取帳號資料失敗';
        }
      } catch (error) {
        console.error('獲取帳號資料失敗:', error);
        errorMessage.value = error.message || '獲取帳號資料失敗，請稍後再試';
      } finally {
        loading.value = false;
      }
    };

    // 處理「擁有全部權限」選項變更
    const handleHasAllPermissionsChange = async () => {
      if (form.hasAllPermissions) {
        // 如果已經設定了程式功能，提示用戶並清除
        if (isEdit.value && associatedPrograms.value.length > 0) {
          const confirmed = await showDeleteConfirm(
            '確認清除程式功能設定',
            '勾選「擁有全部權限」後，已設定的程式功能將被清除。確定要繼續嗎？'
          );
          
          if (confirmed) {
            try {
              // 清除所有已設定的程式
              for (const program of associatedPrograms.value) {
                try {
                  await accountProgramService.removeProgram(route.params.id, program.id, 'client');
                } catch (error) {
                  console.error(`移除程式 ${program.id} 失敗:`, error);
                }
              }
              
              // 立即更新帳號的 hasAllPermissions 狀態到資料庫
              await clientAccountService.updateAccount(route.params.id, {
                hasAllPermissions: true,
                permissions: []
              });
              
              await fetchAssociatedPrograms();
              await showSuccess('已清除', '程式功能設定已清除，權限狀態已更新');
            } catch (error) {
              console.error('更新權限狀態失敗:', error);
              await showError('更新失敗', '清除程式功能成功，但更新權限狀態失敗，請手動點擊「更新帳號」按鈕');
            }
          } else {
            // 用戶取消，恢復勾選狀態
            form.hasAllPermissions = false;
          }
        } else if (isEdit.value) {
          // 如果沒有程式功能需要清除，但還是要更新 hasAllPermissions 狀態
          try {
            await clientAccountService.updateAccount(route.params.id, {
              hasAllPermissions: true,
              permissions: []
            });
            await showSuccess('已更新', '權限狀態已更新為「擁有全部權限」');
          } catch (error) {
            console.error('更新權限狀態失敗:', error);
            await showError('更新失敗', '更新權限狀態失敗，請手動點擊「更新帳號」按鈕');
          }
        }
      } else {
        // 取消勾選「擁有全部權限」時，不需要立即更新資料庫
        // 等待用戶點擊「更新帳號」按鈕時再一起更新
      }
    };

    // 載入帳號的程式列表
    const fetchAssociatedPrograms = async () => {
      if (!isEdit.value || !route.params.id) return;

      loadingPrograms.value = true;
      try {
        const response = await accountProgramService.getProgramsByAccountId(route.params.id, 'client');
        if (response && response.success) {
          // 確保 permissions 是陣列格式（如果是 JSONB 字串需要解析）
          const programs = (response.data || []).map(program => {
            let permissions = program.permissions;
            if (typeof permissions === 'string') {
              try {
                permissions = JSON.parse(permissions);
              } catch (e) {
                console.error('Failed to parse permissions for program:', program.id, e);
                permissions = [];
              }
            }
            if (!Array.isArray(permissions)) {
              permissions = [];
            }
            return {
              ...program,
              permissions
            };
          });
          associatedPrograms.value = programs;
        }
      } catch (error) {
        console.error('載入關聯程式失敗:', error);
      } finally {
        loadingPrograms.value = false;
      }
    };

    // 載入可選程式列表（根據客戶的方案模組）
    const fetchAvailablePrograms = async () => {
      if (!form.clientId || !isEdit.value || !route.params.id) return;

      loadingAvailablePrograms.value = true;
      try {
        // 編輯模式下，使用帳號ID查詢
        const response = await accountProgramService.getAvailablePrograms(route.params.id, null, 'client');
        if (response && response.success) {
          availablePrograms.value = response.data || [];
        }
      } catch (error) {
        console.error('載入可選程式失敗:', error);
        await showError('載入失敗', error.message || '載入可選程式失敗');
        availablePrograms.value = [];
      } finally {
        loadingAvailablePrograms.value = false;
      }
    };

    // 打開添加程式對話框
    const openAddProgramsDialog = async () => {
      if (!form.clientId) {
        await showError('請先選擇客戶', '需要選擇客戶才能添加程式');
        return;
      }
      await fetchAvailablePrograms();
      showAddProgramsDialog.value = true;
    };

    // 處理打開添加程式對話框（新增模式下顯示提示）
    const handleOpenAddProgramsDialog = async () => {
      if (!isEdit.value) {
        await showError('無法操作', '請先保存帳號後才能設定程式功能');
        return;
      }
      await openAddProgramsDialog();
    };

    // 添加程式到帳號
    const handleAddPrograms = async (data) => {
      // 編輯模式下，直接保存到後端
      if (!isEdit.value || !route.params.id) return;

      addingPrograms.value = true;
      try {
        const response = await accountProgramService.addPrograms(
          route.params.id,
          data.programIds,
          data.programPermissions,
          'client'
        );

        if (response && response.success) {
          await showSuccess('添加成功', `成功添加 ${response.data.added} 個程式功能`);
          showAddProgramsDialog.value = false;
          await fetchAssociatedPrograms();
          await fetchAvailablePrograms();
        } else {
          await showError('添加失敗', response?.message || '添加程式失敗');
        }
      } catch (error) {
        console.error('添加程式失敗:', error);
        await showError('添加失敗', error.message || '添加程式失敗');
      } finally {
        addingPrograms.value = false;
      }
    };

    // 從帳號移除程式
    const handleRemoveProgram = async (programId) => {
      // 編輯模式下，從後端移除
      if (!isEdit.value || !route.params.id) return;

      const confirmed = await showDeleteConfirm('確認移除', '確定要從此帳號移除這個程式嗎？');
      if (!confirmed) return;

      removingProgramId.value = programId;
      try {
        const response = await accountProgramService.removeProgram(route.params.id, programId, 'client');
        if (response && response.success) {
          await showSuccess('移除成功', '程式已從帳號中移除');
          await fetchAssociatedPrograms();
          await fetchAvailablePrograms();
        } else {
          await showError('移除失敗', response?.message || '移除程式失敗');
        }
      } catch (error) {
        console.error('移除程式失敗:', error);
        await showError('移除失敗', error.message || '移除程式失敗');
      } finally {
        removingProgramId.value = null;
      }
    };

    // 格式化權限名稱
    const formatPermissionName = (permission) => {
      const parts = permission.split(':');
      if (parts.length !== 2) return permission;
      
      const actionMap = {
        'read': '讀取',
        'create': '新增',
        'update': '編輯',
        'delete': '刪除',
        'export': '匯出',
        'print': '列印'
      };
      
      const resource = parts[0];
      const action = parts[1];
      
      return `${resource}:${actionMap[action] || action}`;
    };

    // 檢查是否所有權限都被勾選
    const isAllPermissionsSelected = (program) => {
      if (!program.allowedPermissions || program.allowedPermissions.length === 0) {
        return true;
      }
      
      if (!program.permissions || program.permissions.length === 0) {
        return true;
      }
      
      const allowedSet = new Set(program.allowedPermissions);
      const programSet = new Set(program.permissions);
      
      return allowedSet.size === programSet.size && 
             Array.from(programSet).every(perm => allowedSet.has(perm));
    };

    // 打開編輯權限對話框
    const handleEditPermissions = (program) => {
      console.log('handleEditPermissions called with program:', program);
      editingProgram.value = program;
      
      // 確保 permissions 是陣列格式（如果是 JSONB 字串需要解析）
      let programPermissions = program.permissions;
      if (typeof programPermissions === 'string') {
        try {
          programPermissions = JSON.parse(programPermissions);
        } catch (e) {
          console.error('Failed to parse permissions:', e);
          programPermissions = [];
        }
      }
      if (!Array.isArray(programPermissions)) {
        programPermissions = [];
      }
      
      if (program.allowedPermissions && program.allowedPermissions.length > 0) {
        editPermissionsSelected.value = [...program.allowedPermissions];
      } else {
        if (programPermissions && programPermissions.length > 0) {
          const readPermission = programPermissions.find(p => p.endsWith(':read'));
          editPermissionsSelected.value = readPermission ? [readPermission] : [];
        } else {
          editPermissionsSelected.value = [];
        }
      }
      
      // 更新 editingProgram 的 permissions 為解析後的格式
      editingProgram.value = {
        ...program,
        permissions: programPermissions
      };
      
      console.log('Opening edit permissions dialog, editingProgram:', editingProgram.value);
      showEditPermissionsDialog.value = true;
    };

    // 切換編輯權限
    const handleEditPermissionToggle = (permission) => {
      if (editPermissionsSelected.value === null) {
        if (editingProgram.value && editingProgram.value.permissions) {
          editPermissionsSelected.value = [...editingProgram.value.permissions];
        } else {
          editPermissionsSelected.value = [];
        }
      }
      
      if (!Array.isArray(editPermissionsSelected.value)) {
        editPermissionsSelected.value = [];
      }
      
      const perms = editPermissionsSelected.value;
      const index = perms.indexOf(permission);
      
      if (index > -1 && permission.endsWith(':read') && perms.length === 1) {
        return;
      }
      
      if (index > -1) {
        perms.splice(index, 1);
        if (perms.length === 0) {
          if (editingProgram.value && editingProgram.value.permissions) {
            const readPermission = editingProgram.value.permissions.find(p => p.endsWith(':read'));
            if (readPermission) {
              perms.push(readPermission);
            }
          }
        }
      } else {
        perms.push(permission);
      }
    };

    // 全選編輯權限
    const selectAllEditPermissions = () => {
      if (editingProgram.value && editingProgram.value.permissions) {
        editPermissionsSelected.value = [...editingProgram.value.permissions];
      }
    };

    // 保存權限
    const handleSavePermissions = async () => {
      if (!editingProgram.value || !isEdit.value || !route.params.id) return;

      updatingPermissions.value = true;
      try {
        let allowedPermissions;
        if (editPermissionsSelected.value === null) {
          allowedPermissions = null;
        } else if (Array.isArray(editPermissionsSelected.value) && editPermissionsSelected.value.length > 0) {
          allowedPermissions = editPermissionsSelected.value;
        } else {
          if (editingProgram.value.permissions && editingProgram.value.permissions.length > 0) {
            const readPermission = editingProgram.value.permissions.find(p => p.endsWith(':read'));
            allowedPermissions = readPermission ? [readPermission] : null;
          } else {
            allowedPermissions = null;
          }
        }

        const response = await accountProgramService.updateProgramPermissions(
          route.params.id,
          editingProgram.value.id,
          allowedPermissions,
          'client'
        );

        if (response && response.success) {
          await showSuccess('更新成功', '程式權限已更新');
          showEditPermissionsDialog.value = false;
          editingProgram.value = null;
          editPermissionsSelected.value = [];
          await fetchAssociatedPrograms();
        } else {
          await showError('更新失敗', response?.message || '更新權限失敗');
        }
      } catch (error) {
        console.error('更新權限失敗:', error);
        await showError('更新失敗', error.message || '更新權限失敗');
      } finally {
        updatingPermissions.value = false;
      }
    };

    // 監聽客戶變更，重新獲取可選程式
    watch(() => form.clientId, async (newClientId) => {
      if (newClientId && isEdit.value) {
        await fetchAvailablePrograms();
      }
    });

    onMounted(async () => {
      await fetchClients();
      await fetchRoles();
      if (isEdit.value) {
        await fetchAccount();
        await fetchAssociatedPrograms();
        if (form.clientId) {
          await fetchAvailablePrograms();
        }
      }
    });

    return {
      form,
      errors,
      errorMessage,
      loading,
      loadingClients,
      clients,
      roles,
      loadingRoles,
      isEdit,
      handleHasAllPermissionsChange,
      handleSubmit,
      handleCancel,
      // 程式功能管理
      loadingPrograms,
      associatedPrograms,
      showAddProgramsDialog,
      loadingAvailablePrograms,
      availablePrograms,
      addingPrograms,
      removingProgramId,
      openAddProgramsDialog,
      handleOpenAddProgramsDialog,
      handleAddPrograms,
      handleRemoveProgram,
      formatPermissionName,
      isAllPermissionsSelected,
      // 編輯權限
      showEditPermissionsDialog,
      editingProgram,
      editPermissionsSelected,
      updatingPermissions,
      handleEditPermissions,
      handleEditPermissionToggle,
      selectAllEditPermissions,
      handleSavePermissions,
    };
  },
};
</script>

<style scoped>
.create-account-container {
  max-width: 900px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
  padding-bottom: 32px;
  border-bottom: 1px solid #e9ecef;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-row.single-column {
  grid-template-columns: 1fr;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 14px;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.form-control:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.form-hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-bottom: 0;
}

.form-group .checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  cursor: pointer;
  font-weight: normal;
  line-height: 1.25;
}

.form-group .form-checkbox {
  width: 16px;
  height: 16px;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
  cursor: pointer;
}

.permissions-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.permission-item {
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.alert {
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-danger {
  background-color: #fee;
  color: #c33;
  border: 1px solid #fcc;
}

.alert-info {
  background-color: #e7f3ff;
  color: #0d47a1;
  border: 1px solid #90caf9;
}

.alert-warning {
  background-color: #fff8e1;
  color: #e65100;
  border: 1px solid #ffcc80;
}

.email-notice {
  margin-top: 8px;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
}

.email-notice i {
  font-size: 18px;
  flex-shrink: 0;
}

.permission-notice {
  margin-top: 0;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
}

.permission-notice i {
  font-size: 18px;
  flex-shrink: 0;
}

.status-notice {
  margin-top: 8px;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
}

.status-notice i {
  font-size: 18px;
  flex-shrink: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .permissions-list {
    grid-template-columns: 1fr;
  }
}

/* 程式功能管理樣式 */
.programs-management {
  margin-top: 16px;
}

.programs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.programs-description {
  color: #6c757d;
  font-size: 14px;
  margin: 0;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #6c757d;
  font-size: 14px;
}

.loading-state i,
.empty-state i {
  font-size: 48px;
  margin-bottom: 12px;
  color: #adb5bd;
}

.programs-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.program-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f8f9fa;
  transition: all 0.2s;
}

.program-item:hover {
  border-color: #ced4da;
  background: #fff;
}

.program-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.btn-outline-primary {
  background: white;
  color: #007bff;
  border: 1px solid #007bff;
}

.btn-outline-primary:hover:not(:disabled) {
  background: #007bff;
  color: white;
}

.program-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.program-icon {
  font-size: 24px;
  color: #495057;
}

.program-details {
  flex: 1;
}

.program-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.program-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #6c757d;
  align-items: center;
  flex-wrap: wrap;
}

.program-code {
  padding: 2px 8px;
  background: #e9ecef;
  border-radius: 3px;
  font-family: monospace;
}

.program-path-small {
  color: #868e96;
  font-size: 11px;
}

.program-allowed-permissions {
  margin-top: 8px;
  width: 100%;
}

.allowed-perms {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.perms-label {
  font-size: 12px;
  color: #495057;
  font-weight: 500;
}

.perm-badge {
  display: inline-block;
  padding: 3px 10px;
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.all-perms-allowed {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  width: fit-content;
}

.all-perms-allowed i {
  font-size: 14px;
}

/* 編輯權限對話框樣式 */
.program-info-section {
  margin-bottom: 20px;
}

.program-info-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.program-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.program-icon-large {
  font-size: 32px;
  color: #495057;
}

.program-details-full {
  flex: 1;
}

.program-name-large {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.program-meta-full {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #6c757d;
}

.permissions-editor {
  margin-top: 20px;
}

.permissions-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.permissions-label {
  font-weight: 500;
  color: #374151;
}

.btn-link {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0;
  text-decoration: underline;
}

.btn-link:hover {
  color: #2563eb;
}

.btn-link.btn-sm {
  font-size: 0.8125rem;
}

.permissions-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.permission-checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 0.875rem;
}

.permission-name {
  color: #374151;
}

.permissions-hint {
  font-size: 0.8125rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

.no-permissions {
  padding: 20px;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

.modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999 !important;
  margin: 0 !important;
  padding: 0 !important;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: relative;
  margin: 0;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #f8f9fa;
  color: #333;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}
</style>
